import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import * as fromStore from '../store';
import { ResponseStatus, SelectItem, uniqBy } from '../../../shared';
import { LoggingService } from '../../../shared/services/logging';

type EmployeeBaseRateViewItem = fromStore.EmployeeBaseRateStateItem & {
    Status: {
        StatusCode: ResponseStatus;
        Message: string;
    };
};

@Component({
    selector: 'ef-employee-base-rate-presenter',
    templateUrl: './employee-base-rate-presenter.component.html',
    styleUrls: ['./employee-base-rate-presenter.component.scss'],
    standalone: false
})
export class EmployeeBaseRatePresenterComponent {
    private _rows: fromStore.EmployeeBaseRateStateItem[] | null = [];

    @Input()
    set rows(value: fromStore.EmployeeBaseRateStateItem[] | null) {
        this._rows = value;
        // NgRx-provided objects may be frozen/immutable; use a mutable copy for editing.
        this.viewRows = (value || [])
            .filter(r => (r?.EmpRateType || '').trim().toUpperCase() !== 'CON')
            .map(r => ({
                ...r,
                Status: {
                    StatusCode: ResponseStatus.None,
                    Message: ''
                }
            }));
        this.employeeSelectItems = uniqBy(
            this.viewRows.map(r => ({
                label: r.EmployeeName,
                value: r.EmployeeListID
            })),
            x => x.value
        ).sort((a, b) =>
            (a?.label ?? '')
                .toString()
                .localeCompare((b?.label ?? '').toString())
        );
    }
    get rows(): fromStore.EmployeeBaseRateStateItem[] | null {
        return this._rows;
    }

    viewRows: EmployeeBaseRateViewItem[] = [];
    employeeSelectItems: SelectItem[] = [];

    @Output() Save = new ReplaySubject<fromStore.EmployeeBaseRateStateItem>(1);
    @Output() Cancel = new EventEmitter<fromStore.EmployeeBaseRateStateItem>();

    editing: EmployeeBaseRateViewItem | null = null;
    cloned: Record<string, EmployeeBaseRateViewItem> = {};

    constructor(private log: LoggingService) {}

    onEditInit($event: { data: EmployeeBaseRateViewItem }): void {
        this.editing = { ...$event.data };
    }

    onRowEditInit(rowData: EmployeeBaseRateViewItem): void {
        this.cloned[rowData.EmployeeListID] = { ...rowData };
        this.editing = rowData;
        this.log.verbose('onRowEditInit');
    }

    onRowEditSave(event: any): void {
        // PrimeNG table event is typically `{ data: row, ... }`, but the Save button also calls this with the row.
        const rowData: EmployeeBaseRateViewItem =
            event && typeof event === 'object' && 'data' in event
                ? (event as { data: EmployeeBaseRateViewItem }).data
                : (event as EmployeeBaseRateViewItem);

        this.Save.next({
            EmployeeListID: rowData.EmployeeListID,
            EmployeeName: rowData.EmployeeName,
            EmpRateType: rowData.EmpRateType,
            BaseRate: rowData.BaseRate
        });
        delete this.cloned[rowData.EmployeeListID];
        delete this.editing;
        this.log.verbose('onRowEditSave');
    }

    onRowEditCancel(item: EmployeeBaseRateViewItem): void {
        const restored = this.cloned[item.EmployeeListID];
        if (restored) {
            this.Cancel.emit({
                EmployeeListID: restored.EmployeeListID,
                EmployeeName: restored.EmployeeName,
                EmpRateType: restored.EmpRateType,
                BaseRate: restored.BaseRate
            });
        }
        delete this.cloned[item.EmployeeListID];
        delete this.editing;
        this.log.verbose('onRowEditCancel');
    }
}
